import React, { forwardRef, ReactNode } from 'react';

import { cn } from '~/utils/styles';

interface FloatingActionButtonProps {
  onClick?: () => void;
  children: ReactNode;
  className?: string;
  variant?: 'circular' | 'extended';
  size?: 'small' | 'medium' | 'large';
  disabled?: boolean;
  style?: React.CSSProperties;
  'aria-label'?: string;
}

const FloatingActionButton = forwardRef<HTMLButtonElement, FloatingActionButtonProps>(
  (
    {
      onClick,
      children,
      className,
      variant = 'circular',
      size = 'medium',
      disabled = false,
      style,
      ...props
    },
    ref,
  ) => {
    const baseClasses = cn(
      'inline-flex items-center justify-center font-medium transition-all duration-200 focus:outline-hidden focus:ring-2 focus:ring-offset-2 focus:ring-primary shadow-lg hover:shadow-xl active:shadow-md cursor-pointer',
      'bg-primary text-white hover:bg-primary-dark active:bg-primary-darker',
      'disabled:opacity-50 disabled:cursor-not-allowed disabled:hover:bg-primary',
      {
        // Variant styles
        'rounded-full': variant === 'circular',
        'rounded-full px-4': variant === 'extended',

        // Size styles
        'w-10 h-10 text-sm': size === 'small' && variant === 'circular',
        'w-12 h-12 text-base': size === 'medium' && variant === 'circular',
        'w-14 h-14 text-lg': size === 'large' && variant === 'circular',

        'h-10 text-sm': size === 'small' && variant === 'extended',
        'h-12 text-base': size === 'medium' && variant === 'extended',
        'h-14 text-lg': size === 'large' && variant === 'extended',
      },
      className,
    );

    return (
      <button
        ref={ref}
        onClick={onClick}
        disabled={disabled}
        className={baseClasses}
        style={style}
        {...props}
      >
        {children}
      </button>
    );
  },
);

FloatingActionButton.displayName = 'FloatingActionButton';

export default FloatingActionButton;
